/**
 * ReferenceIndex (v0.2) — every use-site of every symbol, derived by
 * walking the per-file ParsedFile of every workspace file. Powers
 * find-references, rename, code-lens dependent counts, call hierarchy.
 *
 * References recorded:
 *   - process step `from` and `to` qualified-names → the modules and
 *     interfaces they refer to
 *   - module `in <Parent>` clause → reference to the parent module
 */

import type {
  QualifiedName,
  ResolvedModel,
  SourceSpan,
  TopLevelDecl,
} from "@archlang/engine";
import { forEachCallHop, forEachNode } from "@archlang/engine";
import type { Workspace, FileState } from "./workspace.js";
import type { SymbolTable } from "./symbol-table.js";
import { forEachParsedAspectValue } from "./aspect-refs.js";
import { forEachReentryOccurrence, reentryLabelId } from "./reentry-labels.js";

export type ReferenceContext =
  | "processStep"
  | "moduleInParent"
  | "viewInclude"
  | "moduleRef"
  /** Bare label value referencing a module: `aspect netzone: dmz`. */
  | "aspectRef"
  /** String label value joining an aspect: `aspect netzone: "dmz"`. */
  | "aspectMembership"
  /** A `go <label>` re-entering an `as`-named step (PROCESS.md §7). */
  | "reentryLabel";

export interface Reference {
  /** Resolved symbol's id (matches SymbolEntry.id). */
  readonly symbolId: string;
  readonly uri: string;
  readonly span: SourceSpan;
  readonly context: ReferenceContext;
}

export class ReferenceIndex {
  private readonly byId = new Map<string, Reference[]>();

  constructor(workspace: Workspace, symbols: SymbolTable) {
    for (const uri of workspace.uris()) {
      const file = workspace.getFile(uri);
      if (!file) continue;
      this.indexFile(uri, file, symbols);
    }
    // Process-step refs come from the resolved IR (one shared traversal), not a
    // bespoke per-file AST re-walk — the per-file pass above now records only
    // module structure (`in <Parent>`).
    this.indexProcessRefs(workspace.model(), symbols);
    this.indexAspectValues(workspace, symbols);
    this.indexReentryLabels(workspace);
  }

  /**
   * Record every `go <label>` as a reference to its process-local re-entry
   * symbol (PROCESS.md §7). The `as`-definition is the symbol's declaration
   * (SymbolEntry.nameSpan), surfaced separately by the providers'
   * includeDeclaration path — so only the `go` use-sites are recorded here, to
   * avoid double-counting the def. The id uses the SAME procKey scheme as the
   * SymbolTable, so the generic find-references / rename path lines up.
   */
  private indexReentryLabels(workspace: Workspace): void {
    forEachReentryOccurrence(workspace, (occ) => {
      if (occ.role !== "go") return;
      this.add({
        symbolId: reentryLabelId(occ.procKey, occ.label),
        uri: occ.uri,
        span: occ.span,
        context: "reentryLabel",
      });
    });
  }

  /**
   * Record label-value references. A bare value points at a module (so
   * renaming the module rewrites the label); a string value joins an
   * aspect (so renaming the aspect rewrites every occurrence). The span
   * is the value token — for strings it includes the quotes, and the
   * rename substring-match keeps them.
   */
  private indexAspectValues(workspace: Workspace, symbols: SymbolTable): void {
    forEachParsedAspectValue(workspace, (occ) => {
      if (!occ.valueSpan) return;
      if (occ.cls === "ref" && occ.value.kind === "identifier") {
        // Referrer-aware (v0.13): a twin ref buckets under ITS space's symbol.
        const sym = symbols.resolve(occ.value.text, occ.uri);
        if (sym) {
          this.add({ symbolId: sym.id, uri: occ.uri, span: occ.valueSpan, context: "aspectRef" });
        }
      } else if (occ.cls === "aspect" && occ.value.kind === "string") {
        const asp = symbols.resolveAspect(occ.keyPath, occ.value.text);
        if (asp) {
          this.add({ symbolId: asp.id, uri: occ.uri, span: occ.valueSpan, context: "aspectMembership" });
        }
      }
    });
  }

  referencesTo(id: string): readonly Reference[] {
    return this.byId.get(id) ?? [];
  }

  /** Every recorded reference, flattened — for tests / differential gates. */
  allReferences(): readonly Reference[] {
    const out: Reference[] = [];
    for (const refs of this.byId.values()) out.push(...refs);
    return out;
  }

  private indexFile(uri: string, file: FileState, symbols: SymbolTable): void {
    for (const top of file.file.declarations) {
      this.indexTopLevel(uri, file, top, symbols);
    }
  }

  private indexTopLevel(
    uri: string,
    file: FileState,
    top: TopLevelDecl,
    symbols: SymbolTable
  ): void {
    // Process / subprocess STEP refs are recorded from the resolved IR
    // (indexProcessRefs); the per-file pass records only module structure.
    if (top.kind === "module") {
      this.indexModuleDecl(uri, file, top.decl, symbols);
    } else if (top.kind === "extension") {
      // v0.8.2 §4.16: the `in Target { … }` clause references Target (so find-refs
      // and rename reach it); index the block body's child modules for their refs.
      if (top.decl.targetSource) {
        this.recordQualifiedRef(uri, top.decl.target, top.decl.targetSource, "moduleInParent", symbols);
      }
      for (const sub of top.decl.body.modules ?? []) {
        this.indexModuleDecl(uri, file, sub, symbols);
      }
    }
  }

  /**
   * Record process-step references from the resolved IR — ONE shared traversal
   * (`forEachCallHop`/`forEachNode`), not a bespoke per-file AST re-walk. Rename
   * rewrites these spans, so we record the WRITTEN tokens:
   *   - a hop's caller ref only when it was authored — the chain head when its
   *     owner is `explicit` (`Auth > …`, not a ditto/inherited/omitted head),
   *     and every later chain hop (the parser-threaded module is always written);
   *   - the callee path (the written form: module path for an unspecified-
   *     interface callee, else the interface path) + its owner prefix;
   *   - a note's explicit actor lane; a `do`'s target + its explicit
   *     template-caller prefix.
   * Spans come straight from the IR (`callerSource` / `callee.text.source` /
   * `owner.source` / `targetSource`), identical to the authored tokens.
   */
  private indexProcessRefs(model: ResolvedModel, symbols: SymbolTable): void {
    const bodies = [...(model.processModels ?? []), ...(model.subprocessModels ?? [])];
    for (const pm of bodies) {
      const uri = pm.source?.file ?? "";
      forEachCallHop(pm.body, (hop, node) => {
        const isHead = node.hops[0] === hop;
        const callerWritten = isHead ? node.owner.form === "explicit" : true;
        if (callerWritten && hop.callerSource) {
          this.recordQualifiedRef(uri, hop.caller, hop.callerSource, "processStep", symbols);
        }
        const to = hop.callee.anonymous ? hop.callee.modulePath : hop.callee.interfacePath;
        this.recordQualifiedRef(uri, to, hop.callee.text.source, "processStep", symbols, true);
      });
      forEachNode(pm.body, (n) => {
        if (n.kind === "note" && n.owner.form === "explicit" && n.owner.resolved) {
          this.recordQualifiedRef(uri, n.owner.resolved, n.owner.source, "processStep", symbols);
        } else if (n.kind === "splice") {
          this.recordQualifiedRef(uri, n.target, n.targetSource, "processStep", symbols);
          if (n.templateCaller?.form === "explicit" && n.templateCaller.resolved) {
            this.recordQualifiedRef(uri, n.templateCaller.resolved, n.templateCaller.source, "processStep", symbols);
          }
        }
      });
    }
  }

  private indexModuleDecl(
    uri: string,
    file: FileState,
    decl: {
      parentPath?: QualifiedName;
      parentSource?: SourceSpan;
      body: { modules?: readonly ModuleBodyLike[] };
    },
    symbols: SymbolTable
  ): void {
    if (decl.parentPath && decl.parentSource) {
      this.recordQualifiedRef(uri, decl.parentPath, decl.parentSource, "moduleInParent", symbols);
    }
    // Module-scoped process / subprocess STEP refs are recorded from the IR
    // (indexProcessRefs). Here we only carry the module-structure `in <Parent>`
    // refs, recursing child modules for theirs.
    for (const sub of decl.body.modules ?? []) this.indexModuleDecl(uri, file, sub, symbols);
  }

  /**
   * Record a reference to a qualified name. If the reference is to a
   * compound name (e.g., `Orders.OrdersResource.Create`), also record an
   * implicit reference to each prefix that resolves to a real symbol —
   * that lets "find references for Orders" pick up steps that target an
   * interface inside Orders.
   */
  private recordQualifiedRef(
    uri: string,
    path: QualifiedName,
    span: SourceSpan | undefined,
    context: ReferenceContext,
    symbols: SymbolTable,
    alsoRecordOwner: boolean = false
  ): void {
    if (!span) return;
    const key = path.join(".");
    // Resolve from the REFERRING file (v0.13): a twin name buckets under the
    // referrer's own space's symbol, not whichever twin wins the bare map.
    const sym = symbols.resolve(key, uri);
    if (sym) {
      this.add({ symbolId: sym.id, uri, span, context });
    }
    if (alsoRecordOwner && path.length > 1) {
      // Also record references to ancestors of the path so a lookup of
      // "find references to Orders" will see uses of Orders.X.Y.
      for (let i = path.length - 1; i >= 1; i--) {
        const ancestorKey = path.slice(0, i).join(".");
        const ancestor = symbols.resolve(ancestorKey, uri);
        if (ancestor && ancestor.id !== sym?.id) {
          this.add({ symbolId: ancestor.id, uri, span, context });
        }
      }
    }
  }

  private add(ref: Reference): void {
    const list = this.byId.get(ref.symbolId) ?? [];
    list.push(ref);
    this.byId.set(ref.symbolId, list);
  }
}

// =============================================================================
// Types
// =============================================================================

interface ModuleBodyLike {
  parentPath?: QualifiedName;
  parentSource?: SourceSpan;
  body: { modules?: readonly ModuleBodyLike[] };
}
